import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Heart, Menu, X, Search, ChevronRight } from 'lucide-react';

/**
 * Template 5: TARA — Editorial Chic Header
 * Cream bg, charcoal serif logo, editorial nav with underline hover, mobile hamburger
 */
const Header = ({ showBackButton = false, cartCount = 0 }) => {
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navLinks = [
        { label: 'Home', path: '/template5' },
        { label: 'Shop', path: '/template5/shop' },
        { label: 'About', path: '/template5/about' },
        { label: 'Contact', path: '/template5/contact' },
        { label: 'Orders', path: '/template5/orders' },
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Outfit:wght@300;400;500;600&display=swap');
            `}</style>

            <header
                className={`sticky top-0 z-50 w-full transition-all duration-500 ${scrolled ? 'shadow-sm' : ''}`}
                style={{
                    backgroundColor: scrolled ? 'rgba(250, 249, 246, 0.97)' : '#FAF9F6',
                    backdropFilter: scrolled ? 'blur(10px)' : 'none',
                    borderBottom: '1px solid #E4E4E7',
                }}
            >
                {/* Top Announcement Bar */}
                <div className="bg-[#2C2C2C] text-white text-center py-1.5 md:py-2">
                    <p className="text-[10px] md:text-xs tracking-[0.2em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        Complimentary Shipping on Orders Above ₹2,999
                    </p>
                </div>

                <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-3 md:py-4 lg:py-5">
                        {/* Left Nav (Desktop) */}
                        <nav className="hidden lg:flex items-center gap-8">
                            {navLinks.slice(0, 3).map((link) => (
                                <span
                                    key={link.path}
                                    onClick={() => navigate(link.path)}
                                    className="relative text-[#1A1A1A] text-[13px] tracking-[0.12em] uppercase cursor-pointer group"
                                    style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
                                >
                                    {link.label}
                                    <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#2C2C2C] transition-all duration-300 group-hover:w-full" />
                                </span>
                            ))}
                        </nav>

                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setMobileMenuOpen(true)}
                            className="lg:hidden p-1 text-[#1A1A1A]"
                            aria-label="Open menu"
                        >
                            <Menu className="w-5 h-5" />
                        </button>

                        {/* Logo */}
                        <div
                            onClick={() => navigate('/template5')}
                            className="cursor-pointer text-center"
                        >
                            <h1
                                className="text-2xl md:text-3xl lg:text-[32px] text-[#1A1A1A] tracking-[0.08em]"
                                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                            >
                                TARA
                            </h1>
                            <p
                                className="hidden md:block text-[9px] tracking-[0.35em] uppercase text-[#71717A] mt-0.5"
                                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
                            >
                                Editorial Fashion
                            </p>
                        </div>

                        {/* Right Nav (Desktop) */}
                        <nav className="hidden lg:flex items-center gap-8">
                            {navLinks.slice(3).map((link) => (
                                <span
                                    key={link.path}
                                    onClick={() => navigate(link.path)}
                                    className="relative text-[#1A1A1A] text-[13px] tracking-[0.12em] uppercase cursor-pointer group"
                                    style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
                                >
                                    {link.label}
                                    <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#2C2C2C] transition-all duration-300 group-hover:w-full" />
                                </span>
                            ))}
                        </nav>

                        {/* Right Icons */}
                        <div className="flex items-center gap-3 md:gap-4">
                            <Search className="w-[18px] h-[18px] text-[#1A1A1A] cursor-pointer hover:text-[#71717A] transition-colors hidden md:block" />
                            <Heart className="w-[18px] h-[18px] text-[#1A1A1A] cursor-pointer hover:text-[#71717A] transition-colors hidden md:block" />
                            <div className="relative cursor-pointer" onClick={() => navigate('/template5/cart')}>
                                <ShoppingCart className="w-[18px] h-[18px] text-[#1A1A1A] hover:text-[#71717A] transition-colors" />
                                {cartCount > 0 && (
                                    <span className="absolute -top-2 -right-2 bg-[#2C2C2C] text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center" style={{ fontFamily: "'Outfit', sans-serif" }}>
                                        {cartCount}
                                    </span>
                                )}
                            </div>
                            <User
                                className="w-[18px] h-[18px] text-[#1A1A1A] cursor-pointer hover:text-[#71717A] transition-colors"
                                onClick={() => navigate('/template5/profile')}
                            />
                        </div>
                    </div>
                </div>
            </header>

            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
                <div className="fixed inset-0 z-[100] lg:hidden">
                    <div
                        className="absolute inset-0 bg-black/30"
                        onClick={() => setMobileMenuOpen(false)}
                    />
                    <div
                        className="absolute top-0 left-0 w-[85%] max-w-[340px] h-full overflow-y-auto"
                        style={{ backgroundColor: '#FAF9F6' }}
                    >
                        {/* Menu Header */}
                        <div className="flex items-center justify-between p-5 border-b border-[#E4E4E7]">
                            <h2
                                className="text-xl text-[#1A1A1A] tracking-[0.08em]"
                                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                            >
                                TARA
                            </h2>
                            <button onClick={() => setMobileMenuOpen(false)} className="p-1 text-[#1A1A1A]">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Menu Links */}
                        <nav className="py-4">
                            {navLinks.map((link) => (
                                <div
                                    key={link.path}
                                    onClick={() => { navigate(link.path); setMobileMenuOpen(false); }}
                                    className="flex items-center justify-between px-5 py-3.5 cursor-pointer hover:bg-[#F5F4F0] transition-colors"
                                >
                                    <span
                                        className="text-[#1A1A1A] text-sm tracking-[0.1em] uppercase"
                                        style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
                                    >
                                        {link.label}
                                    </span>
                                    <ChevronRight className="w-4 h-4 text-[#71717A]" />
                                </div>
                            ))}
                        </nav>

                        {/* Menu Footer */}
                        <div className="px-5 pt-4 border-t border-[#E4E4E7]">
                            <div
                                onClick={() => { navigate('/template5/login'); setMobileMenuOpen(false); }}
                                className="flex items-center gap-3 py-3 cursor-pointer"
                            >
                                <User className="w-4 h-4 text-[#71717A]" />
                                <span className="text-sm text-[#71717A] tracking-wide" style={{ fontFamily: "'Outfit', sans-serif" }}>
                                    Sign In / Register
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Header;
